import {
  LDEvaluationDetail,
  withLDProvider
} from "launchdarkly-react-client-sdk";
import React from "react";
import ReactDOM from "react-dom/client";

import { datadogRum } from "@datadog/browser-rum";
import { enableMapSet } from "immer";

import "components/word-processor-field/word-processor.css";
import {
  handleVitePreloadError,
  pruneStaleChunkReloadMarkers
} from "utils/lazy-with-retry.util";
import { viteEnv } from "utils/vite-env.util";

import App from "./App";
import "./app/chart-js";
import "./index.css";
import "./polyfills";
import reportWebVitals from "./reportWebVitals";
import setupDatadog from "./setup/datadog";
import setupMarkerIO from "./setup/marker-io";
import "./setup/mui";

import "animate.css";

window.global ||= window;

enableMapSet();

const LDProvider = withLDProvider({
  clientSideID: viteEnv.VITE_LAUNCHDARKLY_CLIENT_SIDE_ID!,
  context: {
    kind: "user",
    key: "anonymous",
    anonymous: true
  },

  options: {
    inspectors: [
      {
        type: "flag-used",
        name: "dd-inspector",
        method: (key: string, detail: LDEvaluationDetail) => {
          datadogRum?.addFeatureFlagEvaluation?.(key, detail.value);
        }
      }
    ]
  }
})(App);

// Guarded one-shot reload on failed chunk preloads (ENG-1479) — the previous
// unconditional reload looped forever on persistent local fetch failures
// (DEV-2147). One auto-reload per app version per tab; a new deploy gets a
// fresh budget (stale-version markers pruned here).
pruneStaleChunkReloadMarkers();
window.addEventListener("vite:preloadError", handleVitePreloadError);

window.addEventListener("error", (e) => {
  if (e.message?.includes("ResizeObserver loop")) e.stopImmediatePropagation();
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(<LDProvider />);

// Defer non-critical SDK initialization to reduce TBT.
// Datadog SDK v6 buffers all API calls (addError, setUser, etc.) made before
// .init(), so error boundaries and other early callers are safe — their calls
// are queued and replayed once init completes.
const idleInit =
  typeof requestIdleCallback === "function"
    ? (cb: () => void) => requestIdleCallback(cb, { timeout: 3000 })
    : (cb: () => void) => setTimeout(cb, 1);
idleInit(() => {
  setupDatadog();
  setupMarkerIO();
});
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
